import React, { useRef } from 'react'
import { useDrop } from 'react-dnd'
import type { Point } from 'types/screen'
import * as ItemTypes from '@disco/constants/ItemTypes'

const getNodeCenter = (node: HTMLElement): Point => {
  const rect = node.getBoundingClientRect()
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  }
}

const getOffset = (point1: Point, point2: Point): Point => ({
  x: Math.abs(point1.x - point2.x),
  y: Math.abs(point1.y - point2.y),
})

type Props = {
  accept: typeof ItemTypes[keyof typeof ItemTypes][]
  children: React.ReactNode
  index: number
  onDragOver?: (item: Record<string, any>, index: number) => void
  dragoverItem: any
  dragoverItemIndex: number
  thresholdHandler?: (
    draggableItem: any,
    dragoverItem: any,
    dragInfo: {
      pointer: Point
      entryPoint: Point | null | undefined
      targetClientRect: ClientRect
    },
  ) => boolean
}

const SortableItem = ({
  accept,
  children,
  index,
  onDragOver,
  dragoverItem,
  dragoverItemIndex,
  thresholdHandler,
}: Props): React.ReactElement<'div'> => {
  let entryPoint: Point | null | undefined = null
  const ref = useRef<HTMLDivElement | null | undefined>(null)
  const [, drop] = useDrop(
    () => ({
      accept,
      hover: (item: any, monitor) => {
        if (!monitor.isOver()) entryPoint = null

        if (onDragOver) {
          let isItemCloseToTarget: boolean = true
          const pointer: Point = monitor.getClientOffset()
          // we need to set entry point only once
          if (!entryPoint && pointer) entryPoint = pointer

          if (ref.current && !item.files) {
            const target: Point = getNodeCenter(ref.current)
            const offset: Point = getOffset(target, pointer)
            isItemCloseToTarget =
              offset.x < item.width / 2 && offset.y < item.height / 2
          }

          if (isItemCloseToTarget && ref.current) {
            const targetClientRect = ref.current.getBoundingClientRect()
            const draggingOverPartOfSelection =
              item.isPartOfMultipleSelection &&
              Array.isArray(item.selection) &&
              item.selection.includes(dragoverItemIndex)
            const skipHandling =
              !draggingOverPartOfSelection &&
              thresholdHandler &&
              thresholdHandler(item, dragoverItem, {
                pointer,
                entryPoint,
                targetClientRect,
              })
            if (skipHandling) return
            onDragOver(item, index)
          }
        }
      },
    }),
    [index, accept, onDragOver],
  )
  // @ts-expect-error
  return <div ref={drop(ref)}>{children}</div>
}

export default SortableItem
